import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

import Login from "@/pages/Login";
import AdminLogin from "@/pages/AdminLogin";
import AcceptInvite from "@/pages/AcceptInvite";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

import ContractorLayout from "@/pages/contractor/Layout";
import ContractorDashboard from "@/pages/contractor/Dashboard";
import PurchaseRequestForm from "@/pages/contractor/PurchaseRequestForm";
import PurchaseRequestDetail from "@/pages/contractor/PurchaseRequestDetail";
import PurchaseOrders from "@/pages/contractor/PurchaseOrders";
import PurchaseOrderDetail from "@/pages/contractor/PurchaseOrderDetail";
import RFQForm from "@/pages/contractor/RFQForm";
import RFQDetail from "@/pages/contractor/RFQDetail";
import Catalog from "@/pages/contractor/Catalog";
import VendorNetwork from "@/pages/contractor/VendorNetwork";
import Team from "@/pages/contractor/Team";
import ApprovalRules from "@/pages/contractor/ApprovalRules";
import AuditTrail from "@/pages/contractor/AuditTrail";
import ContractorSettings from "@/pages/contractor/Settings";
import CostCodes from "@/pages/contractor/CostCodes";
import DeliveryAddresses from "@/pages/contractor/DeliveryAddresses";
import RecentlyDeleted from "@/components/RecentlyDeleted";
import PurchasingContexts from "@/pages/contractor/PurchasingContexts";
import Jobs from "@/pages/contractor/Jobs";
import JobDetail from "@/pages/contractor/JobDetail";
import ExpenseLog from "@/pages/contractor/ExpenseLog";
import ExpenseLogForm from "@/pages/contractor/ExpenseLogForm";
import ExpenseLogDetail from "@/pages/contractor/ExpenseLogDetail";
import CardTransactions from "@/pages/contractor/CardTransactions";
import Transactions from "@/pages/contractor/Transactions";
import Accounting from "@/pages/contractor/Accounting";
import PurchaserReport from "@/pages/contractor/PurchaserReport";

import VendorLayout from "@/pages/vendor/Layout";
import VendorDashboard from "@/pages/vendor/Dashboard";
import IncomingOrders from "@/pages/vendor/IncomingOrders";
import OrderDetail from "@/pages/vendor/OrderDetail";
import VendorRFQs from "@/pages/vendor/RFQRequests";
import VendorRFQQuote from "@/pages/vendor/RFQQuoteForm";
import VendorCatalog from "@/pages/vendor/Catalog";
import VendorInvoices from "@/pages/vendor/Invoices";
import VendorInvoiceDetail from "@/pages/vendor/InvoiceDetail";
import VendorTeam from "@/pages/vendor/Team";
import ContractorInvoiceDetail from "@/pages/contractor/InvoiceDetail";
import OrderReview from "@/pages/contractor/OrderReview";

import StaffLayout from "@/pages/staff/Layout";
import StaffDashboard from "@/pages/staff/Dashboard";
import Onboarding from "@/pages/staff/Onboarding";
import VendorLeads from "@/pages/staff/VendorLeads";
import Directories from "@/pages/staff/Directories";
import TransactionMonitor from "@/pages/staff/TransactionMonitor";
import StaffTeam from "@/pages/staff/Team";

function ProtectedRoute({ portal, children }) {
  const { user } = useAuth();
  if (user === null) return <div className="flex items-center justify-center min-h-screen text-slate-400 text-sm">Loading…</div>;
  // Staff sign in on their own page (/admin), not the public /login.
  if (user === false) return <Navigate to={portal === "staff" ? "/admin" : "/login"} replace />;
  if (user.portal !== portal) return <Navigate to={`/${user.portal}`} replace />;
  return children;
}

// Meeting 3 renamed Outside Purchases to Expense Log — old links (emails,
// notifications, bookmarks) still land on the right page.
function LegacyExpenseRedirect({ suffix = "" }) {
  const { id } = useParams();
  return <Navigate to={`/contractor/expense-log${id ? `/${id}` : ""}${suffix}`} replace />;
}

// Meeting 3 merged Invoices into the one Accounting page; an old
// ?view=credits&credit_for=<id> link opens the Credits tab there.
function LegacyInvoicesRedirect() {
  const [params] = useSearchParams();
  const creditFor = params.get("credit_for");
  const to = params.get("view") === "credits" ? `/contractor/accounting?tab=credits${creditFor ? `&credit_for=${creditFor}` : ""}` : "/contractor/accounting";
  return <Navigate to={to} replace />;
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/" element={<Navigate to="/login" replace />} />

        <Route path="/contractor" element={<ProtectedRoute portal="contractor"><ContractorLayout /></ProtectedRoute>}>
          <Route index element={<ContractorDashboard />} />
          <Route path="purchase-requests" element={<Navigate to="/contractor/purchase-orders" replace />} />
          <Route path="purchase-requests/new" element={<PurchaseRequestForm />} />
          <Route path="purchase-requests/:id/edit" element={<PurchaseRequestForm />} />
          <Route path="purchase-requests/:id" element={<PurchaseRequestDetail />} />
          <Route path="purchase-orders" element={<PurchaseOrders />} />
          <Route path="purchase-orders/:id" element={<PurchaseOrderDetail />} />
          <Route path="rfqs" element={<Navigate to="/contractor/purchase-orders" replace />} />
          <Route path="rfqs/new" element={<RFQForm />} />
          <Route path="rfqs/:id" element={<RFQDetail />} />
          <Route path="catalog" element={<Catalog />} />
          <Route path="expense-log" element={<ExpenseLog />} />
          <Route path="expense-log/new" element={<ExpenseLogForm />} />
          <Route path="expense-log/:id/edit" element={<ExpenseLogForm />} />
          <Route path="expense-log/:id" element={<ExpenseLogDetail />} />
          <Route path="outside-purchases" element={<LegacyExpenseRedirect />} />
          <Route path="outside-purchases/new" element={<LegacyExpenseRedirect suffix="/new" />} />
          <Route path="outside-purchases/:id/edit" element={<LegacyExpenseRedirect suffix="/edit" />} />
          <Route path="outside-purchases/:id" element={<LegacyExpenseRedirect />} />
          <Route path="card-transactions" element={<CardTransactions />} />
          <Route path="transactions" element={<Transactions />} />
          <Route path="accounting" element={<Accounting />} />
          <Route path="invoices" element={<LegacyInvoicesRedirect />} />
          <Route path="invoices/:id" element={<ContractorInvoiceDetail />} />
          <Route path="order-review" element={<OrderReview />} />
          <Route path="vendor-network" element={<VendorNetwork />} />
          <Route path="team" element={<Team />} />
          <Route path="approval-rules" element={<ApprovalRules />} />
          <Route path="jobs" element={<Jobs />} />
          <Route path="jobs/:id" element={<JobDetail />} />
          <Route path="cost-codes" element={<CostCodes />} />
          <Route path="delivery-addresses" element={<DeliveryAddresses />} />
          <Route path="recently-deleted" element={<RecentlyDeleted base="/contractor" />} />
          <Route path="purchasing-contexts" element={<PurchasingContexts />} />
          <Route path="audit-trail" element={<AuditTrail />} />
          <Route path="reports/purchaser" element={<PurchaserReport />} />
          <Route path="settings" element={<ContractorSettings />} />
        </Route>

        <Route path="/vendor" element={<ProtectedRoute portal="vendor"><VendorLayout /></ProtectedRoute>}>
          <Route index element={<VendorDashboard />} />
          <Route path="orders" element={<IncomingOrders />} />
          <Route path="orders/:id" element={<OrderDetail />} />
          <Route path="invoices" element={<VendorInvoices />} />
          <Route path="invoices/:id" element={<VendorInvoiceDetail />} />
          <Route path="rfqs" element={<VendorRFQs />} />
          <Route path="rfqs/:id" element={<VendorRFQQuote />} />
          <Route path="catalog" element={<VendorCatalog />} />
          <Route path="team" element={<VendorTeam />} />
          <Route path="recently-deleted" element={<RecentlyDeleted base="/vendor" />} />
        </Route>

        <Route path="/staff" element={<ProtectedRoute portal="staff"><StaffLayout /></ProtectedRoute>}>
          <Route index element={<StaffDashboard />} />
          <Route path="onboarding" element={<Onboarding />} />
          <Route path="vendor-leads" element={<VendorLeads />} />
          <Route path="directories" element={<Directories />} />
          <Route path="transactions" element={<TransactionMonitor />} />
          <Route path="team" element={<StaffTeam />} />
        </Route>

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App;
